import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch } from '~/store/types';
import { selectIsAuthenticated } from '../../sportsbook-root-selectors';
import usePersistedBets from './hooks/sportbook-betslip-use-persisted-bets';
import usePersistedSettings from './hooks/sportsbook-betslip-use-persisted-settings';
import { dispose, initialize, selectItemCount } from './ducks';
import createBetslipAnalyticsMiddleware from './ducks/betslip-analytics-middleware';
import sportsbookDynamicMiddlewares from '../../../../../services/middleware/sportsbook-dynamic-middlewares';
import { selectIsBettingServiceApiReady, selectIsSBApiReady } from '../../api/sportsbook-api-duck';
/**
 * Hook to initialize the betslip.
 * This hook should be used in the root of the application to ensure the betslip is initialized correctly.
 *
 * @returns {{ shouldDisplayBetslip: boolean }} - Whether the betslip should be displayed or not.
 */
const useInitializeBetslip = () => {
	const dispatch: AppDispatch = useDispatch();
	// Sportsbook global
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const isBettingServiceApiReady = useSelector(selectIsBettingServiceApiReady);
	const isSBApiReady = useSelector(selectIsSBApiReady);
	// Betslip details
	const itemCount = useSelector(selectItemCount);
	// Persisted data
	const persistedBets = usePersistedBets(isBettingServiceApiReady && isSBApiReady);
	const settings = usePersistedSettings();
	// Initialize the betslip, whenever both sbapi and betting-service is available
	useEffect(() => {
		if (!isBettingServiceApiReady || !isSBApiReady) {
			return;
		}
		dispatch(initialize(persistedBets, undefined, settings?.keepSelections));

		return () => {
			dispatch(dispose());
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to initialize multiple times
	}, [isAuthenticated, isBettingServiceApiReady, isSBApiReady]);

	// Apply middleware for betslip analytics
	useEffect(() => {
		const betslipMiddleware = createBetslipAnalyticsMiddleware();
		sportsbookDynamicMiddlewares.addMiddleware(betslipMiddleware);

		return () => {
			sportsbookDynamicMiddlewares.removeMiddleware(betslipMiddleware);
		};
	}, []);

	return { shouldDisplayBetslip: isBettingServiceApiReady && isSBApiReady && itemCount > 0 };
};

export default useInitializeBetslip;
